// CDV-019: render the `markdown` block with the standard MD pipeline.
//
// Safety: react-markdown does NOT render raw HTML by default — `<script>`,
// `<iframe>` and `javascript:` URLs are stripped automatically by its
// allowlist plugins. We pass no `rehypeRaw`, no `remarkRehype` overrides,
// so the default safe pipeline applies.
//
// Styling: this project does not load `@tailwindcss/typography`, so we
// supply explicit Tailwind classes for each rendered tag instead of
// relying on the `.prose` class. This also keeps Markdown blocks visually
// consistent with the rest of the design system (which doesn't use the
// typography plugin's defaults anywhere else).

import type { ComponentProps } from 'react'
import Markdown, { type Components } from 'react-markdown'

interface Props { //@usedby:ComposedBody.tsx
  text: string
}

const COMPONENTS: Components = {
  h1: (p) => <h1 className="text-2xl font-semibold text-foreground mt-2 mb-3" {...p} />,
  h2: (p) => <h2 className="text-xl font-semibold text-foreground mt-2 mb-3" {...p} />,
  h3: (p) => <h3 className="text-base font-semibold text-foreground mt-2 mb-2" {...p} />,
  h4: (p) => <h4 className="text-sm font-semibold text-foreground mt-2 mb-2" {...p} />,
  p: (p) => <p className="text-sm text-foreground leading-relaxed mb-2 last:mb-0" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 space-y-1 text-sm text-foreground mb-2" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 space-y-1 text-sm text-foreground mb-2" {...p} />,
  li: (p) => <li className="text-sm text-foreground" {...p} />,
  strong: (p) => <strong className="font-semibold text-foreground" {...p} />,
  em: (p) => <em className="italic" {...p} />,
  code: ({ children, ...rest }: ComponentProps<'code'>) => (
    <code
      className="bg-muted text-foreground px-1 py-0.5 rounded text-[0.85em] font-mono"
      {...rest}
    >
      {children}
    </code>
  ),
  pre: (p) => (
    <pre
      className="bg-muted text-foreground p-3 rounded-lg text-xs font-mono overflow-x-auto mb-2"
      {...p}
    />
  ),
  blockquote: (p) => (
    <blockquote
      className="border-l-2 border-border pl-3 text-foreground-muted italic mb-2"
      {...p}
    />
  ),
  hr: () => <hr className="border-border my-3" />,
  // Force every link to open safely in a new tab.
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:underline"
    >
      {children}
    </a>
  ),
}

export function MarkdownBlockView({ text }: Props) {
  return (
    <div className="text-foreground">
      <Markdown components={COMPONENTS}>{text}</Markdown>
    </div>
  )
}
